import React, {
    createContext,
    Dispatch,
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState
} from 'react';
import { useSelector } from 'react-redux';
import { currentCountrySelector, currentCurrencySelector } from '@selectors/countries.selector';
import { useRestaurantMenu } from "@vms/RestaurantMenu";
import Loader from "@components/Loader/Loader";
import {
    defaultFilterValues,
    FilterInput,
    FilterOutput,
    FilterValues,
    SelectFiltersContextType,
    TooltipItem
} from "@vms/SelectFilters/SelectFiltersTypes";
import { useFiltersText } from "@vms/SelectFilters/useFiltersText";
import { mapFiltersToApi } from "@vms/SelectFilters/mapFiltersToApi";
import { mapFiltersToClient } from "@vms/SelectFilters/mapFiltersToClient";
import { MainFilterOutput } from "@vms/RestaurantMenu/RestaurantMenuContext";
import { useFiltersItems } from "@vms/SelectFilters/useFiltersItems";
import useRestaurants from "@apiHooks/useRestaurants";

export const defaultContextValue: SelectFiltersContextType = {
    activeRestaurantListId: 0,
    setActiveRestaurantList: () => null,
    filters: defaultFilterValues,
    initialFilterValues: defaultFilterValues,
    setPartial: () => null,
    resetFilters: () => null,
    isLoadingFilters: true,
    isFiltersPopupShown: false,
    openFiltersPopup: () => null,
    closeFiltersPopup: () => null,
    filtersText: '',
    countryCode: '',
    currentCurrency: '',
    restaurants: [],
    tooltipsData: [],
    tags: [],
    tagCategories: [],
    keywords: [],
};

export const SelectFiltersContext = createContext<SelectFiltersContextType>(defaultContextValue);

const filterValuesReducer = (
    state: FilterValues,
    change: Partial<FilterValues>,
): FilterValues => {
    return {
        ...defaultFilterValues,
        ...state,
        ...change,
    };
};

export function useSelectFilters(
    initialData: FilterValues = defaultFilterValues
): [
    FilterValues,
    Dispatch<Partial<FilterValues>>
] {
    // @ts-ignore
    return useReducer(filterValuesReducer, initialData);
}

export function getDefaultFilterData(
    restaurantListId: number,
    rawFilterData: MainFilterOutput,
) {
    if (!rawFilterData?.filters?.length) return;

    const defaultListId = restaurantListId || rawFilterData.defaultRestaurantListId;
    return (rawFilterData.filters.find(f => f.restaurantListIds?.includes(defaultListId!)) || rawFilterData.filters[0]);
}

export function useDefaultFilterData(): FilterOutput | undefined {
    const { activeRestaurantListId } = useFilters();
    const { rawFilterData } = useRestaurantMenu();

    return useMemo(
        () => getDefaultFilterData(activeRestaurantListId, rawFilterData),
        [activeRestaurantListId, rawFilterData]
    );
}

export function useMappedFiltersToApi() {
    const { countryCode, filters, activeRestaurantListId } = useFilters();
    const { restaurantLists, customRestaurantLists } = useRestaurantMenu();

    const activeRestaurantList = useMemo(
        () => [
            ...restaurantLists,
            ...customRestaurantLists,
        ].find(list => list.id === activeRestaurantListId),
        [restaurantLists, customRestaurantLists, activeRestaurantListId]
    );

    return useMemo(
        () => mapFiltersToApi(filters, countryCode, activeRestaurantListId, activeRestaurantList?.name ?? ''),
        [filters, countryCode, activeRestaurantListId, activeRestaurantList]
    );
}

const SelectFiltersProvider: FC = ({ children }) => {
    const countryCode = useSelector(currentCountrySelector);
    const currentCurrency = useSelector(currentCurrencySelector);

    const {
        rawFilterData,
        isLoadingRestaurantLists: isLoadingFilters
    } = useRestaurantMenu();

    const [filters, dispatchPartialFilters] = useSelectFilters();
    const [isInitialized, setIsInitialized] = useState(false);

    const { activeRestaurantListId } = filters;

    const { data: allRestaurants } = useRestaurants(countryCode, activeRestaurantListId);

    const setActiveRestaurantList = useCallback(
        (id: number, apiFiltersValues?: FilterInput) => {
            const value = mapFiltersToClient(
                id,
                getDefaultFilterData(id, rawFilterData),
                apiFiltersValues,
                false
            );
            dispatchPartialFilters(value);
        },
        [dispatchPartialFilters, rawFilterData]
    );

    useEffect(() => {
        if (rawFilterData?.defaultRestaurantListId) {
            setActiveRestaurantList(rawFilterData.defaultRestaurantListId);
            setTimeout(() => {
                setIsInitialized(true);
            }, 100);
        }
    }, [rawFilterData?.defaultRestaurantListId, setActiveRestaurantList]);

    const [isFiltersPopupShown, setIsFiltersPopupShown] = useState(false);

    const openFiltersPopup = useCallback(() => {
        setIsFiltersPopupShown(true);
    }, []);

    const closeFiltersPopup = useCallback(() => {
        setIsFiltersPopupShown(false);
    }, []);

    const initialFilterValues = useMemo(
        () => mapFiltersToClient(
            activeRestaurantListId,
            getDefaultFilterData(activeRestaurantListId, rawFilterData),
            undefined,
             false
        ),
        [activeRestaurantListId, rawFilterData]
    );

    const resetFilters = useCallback(() => {
        dispatchPartialFilters(initialFilterValues);
    }, [dispatchPartialFilters, initialFilterValues]);

    const tooltipsData: TooltipItem[] = useMemo(() => rawFilterData?.tooltips || [], [rawFilterData]);

    const { keywords, tags, tagCategories } = useFiltersItems();

    const filtersText = useFiltersText(filters);

    const value = useMemo<SelectFiltersContextType>(() => ({
        activeRestaurantListId,
        setActiveRestaurantList,
        filters,
        initialFilterValues,
        setPartial: dispatchPartialFilters,
        resetFilters,
        isLoadingFilters,
        isFiltersPopupShown,
        openFiltersPopup,
        closeFiltersPopup,
        filtersText,
        countryCode,
        currentCurrency,
        restaurants: allRestaurants ?? [],
        tooltipsData,
        tags,
        tagCategories,
        keywords,
    }), [
        activeRestaurantListId,
        setActiveRestaurantList,
        filters,
        initialFilterValues,
        dispatchPartialFilters,
        resetFilters,
        isLoadingFilters,
        isFiltersPopupShown,
        openFiltersPopup,
        closeFiltersPopup,
        filtersText,
        countryCode,
        currentCurrency,
        allRestaurants,
        tooltipsData,
        tags,
        tagCategories,
        keywords,
    ]);

    if (isLoadingFilters || !isInitialized) {
        return (
            <Loader
                isShown
                noBg
            />
        );
    }

    return (
        <SelectFiltersContext.Provider value={value}>
            {children}
        </SelectFiltersContext.Provider>
    );
};

export const useFilters = () => {
    const context = useContext(SelectFiltersContext);
    if (!context) {
        throw new Error('useFilters must be used within SelectFiltersProvider');
    }
    return context;
};

export default SelectFiltersProvider;
